// src/pages/overview/widgets/DealSelector.jsx
import React, { useState, useRef, useEffect, useMemo } from "react";
import { IoChevronDown } from "react-icons/io5";
import { useDealData } from "contexts/DealContext";
import { IoAdd, IoTrash } from "react-icons/io5";
import TooltipHorizon from "components/tooltip";
import GroupCreator from "pages/overview/groups/GroupCreator";

const DealSelector = ({
  dealNames = [],
  value,
  onChange,
  size,
  currentGroup,
  username,
  isDealLoading,
}) => {
  const { groups = [], currentDealName, deleteGroup, setCurrentGroup, viewingArchived } = useDealData();
  const [showGroupCreator, setShowGroupCreator] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);
  const optionsRef = useRef([]);
  const [displayValue, setDisplayValue] = useState("Select Your Deal");

  // Group the facility groups by dealId
  const groupsByDeal = groups ? groups.reduce((acc, group) => {
    if (!acc[group.dealId]) {
      acc[group.dealId] = [];
    }
    acc[group.dealId].push(group);
    return acc;
  }, {}) : {};

  // Get the placeholder text based on the archive status
  const getPlaceholderText = () => {
    if (viewingArchived) {
      return "Select Archived Deal";
    }
    return "Select Your Deal";
  };

  // Safely wrap dealNames to ensure it's always an array
  const safeNames = Array.isArray(dealNames) ? dealNames : [];

  // Prepare flattened options list for keyboard navigation
  const flattenedOptions = useMemo(() => [
    { id: "placeholder", value: "", label: getPlaceholderText(), isGroup: false, groupId: null },
    ...safeNames.flatMap(dealName => [
      { id: dealName, value: dealName, label: dealName, isGroup: false, groupId: null },
      ...(groupsByDeal[dealName] || []).map(group => ({
        id: group.id,
        value: group.id,
        label: group.name,
        isGroup: true,
        groupId: group.id,
        parentDeal: dealName
      }))
    ])
  ], [safeNames, groupsByDeal, viewingArchived]);

  // Update display value when value changes
  useEffect(() => {
    if (currentGroup) {
      // If we have a current group, always use that for display
      setDisplayValue(`${currentGroup.name} (Group)`);
      return;
    }

    // Handle empty value (including empty string)
    if (!value || value === "") {
      setDisplayValue(getPlaceholderText());
      return;
    }

    // Check if value indicates a group
    if (value.startsWith('group-')) {
      // It's a group - find its name
      const group = groups.find(g => g.id === value);
      if (group) {
        setDisplayValue(`${group.name} (Group)`);
      } else {
        setDisplayValue(value);
      }
    } else {
      // It's a regular deal
      setDisplayValue(value);
    }
  }, [value, groups, currentGroup, viewingArchived]);

  // Click outside handler for both dropdown and modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (showGroupCreator && modalRef.current && !modalRef.current.contains(event.target)) {
        setShowGroupCreator(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showGroupCreator]);

  // Update refs array when options change
  useEffect(() => {
    optionsRef.current = optionsRef.current.slice(0, flattenedOptions.length);
  }, [flattenedOptions]);

  // Keyboard event handler
  const handleKeyDown = (e) => {
    // If the modal is open, don't handle keyboard events for the dropdown
    if (showGroupCreator) {
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    // Calculate total number of options (placeholder + deals + groups)
    const totalOptions = 1 + safeNames.reduce((total, dealName) => {
      return total + 1 + (groupsByDeal[dealName]?.length || 0);
    }, 0);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prevIndex =>
          prevIndex < totalOptions - 1 ? prevIndex + 1 : 0
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prevIndex =>
          prevIndex > 0 ? prevIndex - 1 : totalOptions - 1
        );
        break;

      case 'Enter':
      case ' ':
        e.preventDefault();
        if (activeIndex === 0) {
          // Handle placeholder selection
          handleSelect("", false, null, null);
        } else {
          // Find the corresponding option based on the active index
          let currentIndex = 1; // Start after placeholder
          for (const dealName of safeNames) {
            if (activeIndex === currentIndex) {
              // Deal selection
              handleSelect(dealName, false, null, null);
              break;
            }
            currentIndex++;

            // Check groups for this deal
            const dealGroups = groupsByDeal[dealName] || [];
            for (const group of dealGroups) {
              if (activeIndex === currentIndex) {
                // Group selection
                handleSelect(group.id, true, group.id, dealName);
                break;
              }
              currentIndex++;
            }
          }
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;

      case 'Tab':
        setIsOpen(false);
        break;

      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;

      case 'End':
        e.preventDefault();
        setActiveIndex(totalOptions - 1);
        break;

      default:
        const key = e.key.toLowerCase();
        if (key.length === 1 && /[a-z0-9]/.test(key)) {
          // Find the next option that starts with the key
          let currentIndex = 0;
          let foundIndex = -1;

          // Check placeholder
          if (getPlaceholderText().toLowerCase().startsWith(key)) {
            foundIndex = 0;
          }

          // Check deals and groups
          if (foundIndex === -1) {
            for (const dealName of safeNames) {
              currentIndex++;
              if (dealName.toLowerCase().startsWith(key)) {
                foundIndex = currentIndex;
                break;
              }

              // Check groups for this deal
              const dealGroups = groupsByDeal[dealName] || [];
              for (const group of dealGroups) {
                currentIndex++;
                if (group.name.toLowerCase().startsWith(key)) {
                  foundIndex = currentIndex;
                  break;
                }
              }
              if (foundIndex !== -1) break;
            }
          }

          if (foundIndex !== -1) {
            setActiveIndex(foundIndex);
          }
        }
        break;
    }
  };

  // Scroll active option into view
  useEffect(() => {
    if (isOpen && activeIndex >= 0 && optionsRef.current[activeIndex]) {
      optionsRef.current[activeIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [activeIndex, isOpen]);

  // Handler for selection changes
  const handleSelect = (selectedValue, isGroup = false, groupId = null, parentDeal = null) => {
    setIsOpen(false);
    // Handle placeholder selection (clearing the selection)
    if (selectedValue === "") {
      setDisplayValue("Select Your Deal");
      onChange({
        target: {
          value: ""
        }
      });
      return;
    }

    if (isGroup) {
      // Find the group in groups (not facilityGroups)
      const selectedGroup = groups.find(g => g.id === groupId);
      if (selectedGroup) {
        onChange({
          target: {
            value: groupId
          },
          isGroup: true,
          groupId: groupId,
          dealId: parentDeal,
          group: selectedGroup // Pass the full group object
        });
      }
    } else {
      onChange({
        target: {
          value: selectedValue
        }
      });
    }
  };

  return (
    <div
      className="relative sm-max:w-full sm:min-w-[225px]"
      onKeyDown={handleKeyDown}
      ref={dropdownRef}
      tabIndex="0"
    >
      {/* Dropdown trigger button */}
      <div className="flex items-center gap-1 w-full">
        <div
          className={`flex w-full items-center justify-between px-2 py-2 bg-white border-b border-[#89949f] cursor-pointer ${size === "small" ? "text-sm" : "text-base"} ${isOpen ? "border-blue-500" : ""} ${isDealLoading ? "opacity-50 cursor-wait" : ""}`}
          onClick={() => !isDealLoading && setIsOpen(!isOpen)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls="deal-selector"
          role="combobox"
        >
          <div className="flex flex-col">
            <span className="truncate overflow-hidden flex items-center gap-2">
              {isDealLoading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  Loading...
                </>
              ) : (
                displayValue
              )}
            </span>
          </div>
          {!isDealLoading && (
            <IoChevronDown className={`w-[12px] h-[12px] transition-transform ml-2 ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </div>

        {currentDealName && !isDealLoading && (
          <div className="flex items-center gap-1">
            <TooltipHorizon
              content="Create a new facility group"
              placement="top"
              extra="text-xs bg-opacity-50 text-gray-700 top-4"
              trigger={
                <button
                  onClick={() => setShowGroupCreator(true)}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded-full"
                  aria-label="Create a new facility group"
                >
                  <IoAdd size={15} />
                </button>
              }
            />
            {/* Delete Group button - only show when a group is selected */}
            {currentGroup && (
              <TooltipHorizon
                content={
                  currentGroup.createdBy === username
                    ? "Delete this facility group"
                    : "Only the creator can delete this group"
                }
                placement="bottom"
                extra="text-xs bg-opacity-50 text-gray-700 top-4"
                trigger={
                  <button
                    onClick={() => {
                      if (currentGroup.createdBy === username &&
                        window.confirm('Are you sure you want to delete this group?')) {
                        deleteGroup(currentGroup.id);
                        setCurrentGroup(null);
                      }
                    }}
                    className={`p-1 rounded-full ${currentGroup.createdBy === username
                      ? "text-red-600 hover:bg-red-50"
                      : "text-gray-400 cursor-not-allowed"
                      }`}
                    disabled={currentGroup.createdBy !== username}
                    aria-label={
                      currentGroup.createdBy === username
                        ? "Delete this facility group"
                        : "Cannot delete this group - only the creator can delete it"
                    }
                  >
                    <IoTrash size={15} />
                  </button>
                }
              />
            )}
          </div>
        )}
      </div>

      {/* Dropdown menu */}
      {isOpen && !isDealLoading && (
        <div
          className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
          role="listbox"
        >
          <ul className="py-1">
            {flattenedOptions.map((option, index) => (
              <li
                key={option.id}
                ref={el => optionsRef.current[index] = el}
                className={`${option.isGroup ? 'pl-8 pr-4 italic text-gray-700' : 'px-4 font-medium'} py-2 ${activeIndex === index ? "bg-blue-100" : "hover:bg-gray-100"
                  } cursor-pointer ${option.id === "placeholder" ? "text-gray-500" : ""}`}
                onClick={() => {
                  if (option.id === "placeholder") {
                    setDisplayValue("Select Your Deal");
                    onChange({
                      target: {
                        value: ""
                      }
                    });
                  } else if (option.isGroup) {
                    onChange({
                      target: {
                        value: option.value
                      },
                      isGroup: true,
                      groupId: option.groupId,
                      dealId: option.parentDeal
                    });
                  } else {
                    onChange({
                      target: {
                        value: option.value
                      }
                    });
                  }
                  setIsOpen(false);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                role="option"
                aria-selected={activeIndex === index}
                tabIndex="-1"
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Group Creator Modal */}
      {showGroupCreator && (
        <div
          className="inset-0 bg-black bg-opacity-50 flex absolute top-[60px] justify-center z-50 w-[400px]"
          ref={modalRef}
        >
          <div className="">
            <GroupCreator onClose={() => setShowGroupCreator(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default DealSelector;